import { autobind } from "core-decorators";
import { Inject } from "typescript-ioc";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";

import { Utils } from "Helpers/Scripts/Utils";
import { PickupPointWithoutMap } from "./PickupPointWithoutMap";
import { PickupPointWithMap } from "./PickupPointWithMap";
import { PickupPointWithMapYandex } from "./PickupPointWithMapYandex";
import * as DeliveryChannelsEventType from "./DeliveryChannelsEventType";

import { IChannelUpdateEvent } from "./IChannelUpdateEvent";
import { IPickupPointModel } from "./IPickupPointModel";
import { IMapSwitcherLocalizations } from "./IMapSwitcherLocalizations";
import { UiComponent } from "Ui/Scripts/UiComponent";
import { ISelectedChannelAddress } from "./ISelectedChannelAddress";

export class PickupPointDeliveryChannel extends UiComponent {
    protected _model: IPickupPointModel;

    public static readonly templateId: string = "pickup-points-delivery-method-template";
    public readonly PICKUP_POINT_FILTERS_WRAPPER: string = ".js-pickup-point-filters-wrapper";

    private readonly DELIVERY_PAGE: string = "#delivery-page";
    private readonly PICKUP_POINT_FILTERS: string = ".js-pickup-point-filters";
    private readonly FILTERS_SEARCH_WRAPPER: string = ".js-filters-search-wrapper";
    private readonly TOGGLE_MAP_BTN: string = ".js-map-switcher";
    private readonly TOGGLE_MAP_ICON: string = ".js-map-switch";
    private readonly LIST_ICON_CLASS: string = "v-icon-list";
    private readonly MAP_ICON_CLASS: string = "v-icon-map-pin-outlined";
    private readonly NO_SHADOW_CLASS: string = "no-shadow";
    private readonly RADIO_ITEM_WRAPPER_CLASS: string = ".w-radio-item";
    private readonly PICKUP_POINT_LAYOUT_LIST: number = 0;
    private readonly PICKUP_POINT_LAYOUT_MAP: number = 1;
    private readonly PICKUP_POINT_LAYOUT_MAP_AND_FALLBACK: number = 2;
    private readonly PICKUP_POINT_START_PAGE: number = 0;
    private readonly MAP_PROVIDER_BING: number = 0;
    private readonly MAP_PROVIDER_YANDEX: number = 1;
    private readonly MAP_PROVIDER_GOOGLE: number = 2;
    private readonly TITLE_ATTR: string = "title";
    private readonly ARIA_LABEL_ATTR: string = "aria-label";
    private readonly DELIVERY_CHANNEL: string = "deliveryChannel";
    private readonly DISABLED_ATTRIBUTE: string = "disabled";
    private readonly KENDO_RADIO_BUTTON: string = "kendoRadioButton";

    private readonly NOT_SUPPORTED_PROVIDER_MESSAGE: string =
        "Configuration issue: Selected map provider is not supported.";

    private _deliveryChannel: number;
    private _pickupPointComponent:
        | PickupPointWithoutMap
        | PickupPointWithMap
        | PickupPointWithMapYandex;
    private _switchToMap: string;
    private _switchToFallback: string;

    public get key(): string {
        return "PickupPointDeliveryChannel";
    }

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory
    ) {
        super(componentFactory, binder, loggerFactory);
    }

    public init(): void {
        this._bindEvents();
        this._setOptions();

        if (
            this._model.mapModule &&
            this._model.mapModule.PickupPointLayoutType === this.PICKUP_POINT_LAYOUT_MAP &&
            this._model.mapModule.MapProvider === this.MAP_PROVIDER_BING
        ) {
            this._createPickupPointWithMap();
        } else if (
            this._model.mapModule &&
            this._model.mapModule.PickupPointLayoutType === this.PICKUP_POINT_LAYOUT_MAP &&
            this._model.mapModule.MapProvider === this.MAP_PROVIDER_YANDEX
        ) {
            this._createPickupPointWithMapYandex();
        } else if (
            this._model.mapModule &&
            this._model.mapModule.PickupPointLayoutType !== this.PICKUP_POINT_LAYOUT_LIST &&
            this._model.mapModule.MapProvider === this.MAP_PROVIDER_GOOGLE
        ) {
            this._createPickupPointWithoutMap();
            this._logger.error(this.NOT_SUPPORTED_PROVIDER_MESSAGE);
        } else {
            this._createPickupPointWithoutMap();
        }
        if (this._model.IsSelected) {
            this._makeChannelSelected();
            if (
                this._pickupPointComponent instanceof PickupPointWithMap ||
                this._pickupPointComponent instanceof PickupPointWithMapYandex
            ) {
                this._pickupPointComponent.initMap();
            }
        }
    }

    private _makeChannelSelected(): void {
        const input = this.findElement("input:radio");
        if (input && !input.hasAttribute(this.DISABLED_ATTRIBUTE)) {
            Utils.getData(input, this.KENDO_RADIO_BUTTON).value(true);
        }
    }

    private _setOptions(): void {
        const mapSwitchLocs = Utils.getData(
            this.DELIVERY_PAGE,
            "map-switcher"
        ) as IMapSwitcherLocalizations;
        this._switchToMap = mapSwitchLocs.switchToMap;
        this._switchToFallback = mapSwitchLocs.switchToFallback;
    }

    private _createPickupPointWithMap(): void {
        this._pickupPointComponent = this.createComponent(
            PickupPointWithMap,
            this.PICKUP_POINT_FILTERS_WRAPPER,
            this._model
        );
    }

    private _createPickupPointWithMapYandex(): void {
        this._pickupPointComponent = this.createComponent(
            PickupPointWithMapYandex,
            this.PICKUP_POINT_FILTERS_WRAPPER,
            this._model
        );
    }

    private _createPickupPointWithoutMap(): void {
        this._pickupPointComponent = this.createComponent(
            PickupPointWithoutMap,
            this.PICKUP_POINT_FILTERS_WRAPPER,
            this._model
        );
    }

    private _bindEvents(): void {
        this.bindClick(this._handleClick);
        this._binder.bindCustomEvent(
            DeliveryChannelsEventType.PICKUP_POINT_CHANNEL_UPDATE,
            this._handlePickupChannelUpdate
        );
        this._binder.bindCustomEvent(
            DeliveryChannelsEventType.MAP_LOAD_FAILER,
            this._handleMapLoadFail,
            "",
            true
        );
        if (
            this._model.mapModule &&
            this._model.mapModule.PickupPointLayoutType ===
                this.PICKUP_POINT_LAYOUT_MAP_AND_FALLBACK &&
            this._model.mapModule.MapProvider !== this.MAP_PROVIDER_GOOGLE
        ) {
            this.bindDelegatedClick(this.TOGGLE_MAP_BTN, this._toggleMap);
        }
    }

    @autobind
    private _toggleMap(): void {
        const filterIndex = this._pickupPointComponent.getFilterTypeValue();
        this._pickupPointComponent.toggleMapWrapper();
        this._pickupPointComponent.toggleSearchButton();
        this._pickupPointComponent.destroyPickupPointComponent();
        this._updateSwitcherBtn();
        if (this._pickupPointComponent.isMapShown === true) {
            this._createPickupPointWithoutMap();
            this._pickupPointComponent.isMapShown = false;
            if (
                this._model.mapModule &&
                ori.maps &&
                this._model.mapModule.MapProvider === this.MAP_PROVIDER_YANDEX
            ) {
                ori.maps.destroyMap();
            }
        } else {
            if (this._pickupPointComponent instanceof PickupPointWithoutMap) {
                this._pickupPointComponent.hideNoPickPointsFoundElement();
            }
            if (
                this._model.mapModule &&
                this._model.mapModule.MapProvider === this.MAP_PROVIDER_BING
            ) {
                this._createPickupPointWithMap();
            }
            if (
                this._model.mapModule &&
                this._model.mapModule.MapProvider === this.MAP_PROVIDER_YANDEX
            ) {
                this._createPickupPointWithMapYandex();
            }
            if (
                this._pickupPointComponent instanceof PickupPointWithMap ||
                this._pickupPointComponent instanceof PickupPointWithMapYandex
            ) {
                this._pickupPointComponent.initMap();
                try {
                    this._pickupPointComponent.loadAndShowPickupPoints();
                } catch (e) {
                    this._logger.info("map not initialized");
                }
            }
            this._pickupPointComponent.isMapShown = true;
        }
        this._pickupPointComponent.setTextSearchValue();
        if (this._model.mapModule && this._model.mapModule.MapProvider === this.MAP_PROVIDER_BING) {
            this._pickupPointComponent.setFilterTypeValue(filterIndex);
        }
    }

    @autobind
    private _updateSwitcherBtn(): void {
        const switchIcon = this.findElement(this.TOGGLE_MAP_ICON);
        if (switchIcon !== null && !Utils.hasClass(switchIcon, this.LIST_ICON_CLASS)) {
            Utils.addClass(switchIcon, this.LIST_ICON_CLASS);
            Utils.removeClass(switchIcon, this.MAP_ICON_CLASS);
            Utils.setAttr(switchIcon, this.TITLE_ATTR, this._switchToFallback);
            Utils.setAttr(switchIcon, this.ARIA_LABEL_ATTR, this._switchToFallback);
        } else if (switchIcon !== null) {
            Utils.removeClass(switchIcon, this.LIST_ICON_CLASS);
            Utils.addClass(switchIcon, this.MAP_ICON_CLASS);
            Utils.setAttr(switchIcon, this.TITLE_ATTR, this._switchToMap);
            Utils.setAttr(switchIcon, this.ARIA_LABEL_ATTR, this._switchToMap);
        }
    }

    @autobind
    private _handleMapLoadFail(): void {
        // Reposition the filters
        const pickupPointFilters = this.findElement(this.PICKUP_POINT_FILTERS);
        const filterSearchWrapper = this.findElement(this.FILTERS_SEARCH_WRAPPER);

        if (pickupPointFilters && filterSearchWrapper) {
            Utils.append(pickupPointFilters, filterSearchWrapper);
        }
        this._createPickupPointWithoutMap();
    }

    private _isClickOnChannel(event: IChannelUpdateEvent): boolean {
        return (<HTMLInputElement>event.target).name === this.DELIVERY_CHANNEL;
    }

    private _triggerChannelUpdate(data: ISelectedChannelAddress): void {
        this._binder.trigger(DeliveryChannelsEventType.CHANNEL_UPDATE, data);
    }

    @autobind
    private _handleClick(event: IChannelUpdateEvent): void {
        if (this._isClickOnChannel(event)) {
            if (
                this._pickupPointComponent instanceof PickupPointWithMap ||
                this._pickupPointComponent instanceof PickupPointWithMapYandex
            ) {
                this._pickupPointComponent.initMap();
            }
            this._triggerChannelUpdate({
                deliveryChannel: this._deliveryChannel,
                deliveryAddress: null,
            });
        }
    }

    @autobind
    private _handlePickupChannelUpdate(event: IChannelUpdateEvent): void {
        event.stopPropagation();
        this._deliveryChannel = event.deliveryChannel;

        const radioItemWrapper = this.findElement(this.RADIO_ITEM_WRAPPER_CLASS);
        if (radioItemWrapper) {
            Utils.toggleClass(
                radioItemWrapper,
                this.NO_SHADOW_CLASS,
                Boolean(this._deliveryChannel)
            );
        }

        this._triggerChannelUpdate({
            deliveryAddress: null,
            deliveryChannel: this._deliveryChannel,
        });
    }

    public update(): void {
        if (this._pickupPointComponent instanceof PickupPointWithoutMap) {
            this._pickupPointComponent.update(null, this.PICKUP_POINT_START_PAGE);
        } else {
            this._pickupPointComponent.update();
        }
    }
}
